'use client';

import { useEffect, useState } from 'react';

import { ArrowRight } from 'lucide-react';

/**
 * Renders a forward-pointing arrow that flips horizontally in RTL contexts
 * so it always points in the reading direction. Drop-in replacement for
 * `<ArrowRight />` everywhere "go forward" / "see more" semantics matter.
 *
 *   <DirectionalArrow size={12} strokeWidth={1.5} />
 *
 * Reads `document.dir` once on mount and listens for changes via the
 * MutationObserver on <html> — this is necessary because i18n locale
 * switches in this app re-write `dir` on the root html element rather
 * than re-mounting the tree.
 */
export function DirectionalArrow({
  size = 14,
  strokeWidth = 1.4,
  color,
}: {
  size?: number;
  strokeWidth?: number;
  color?: string;
}) {
  const isRtl = useIsRtl();
  return (
    <ArrowRight
      size={size}
      strokeWidth={strokeWidth}
      color={color}
      style={{ transform: isRtl ? 'scaleX(-1)' : undefined }}
    />
  );
}

function useIsRtl() {
  const [isRtl, setIsRtl] = useState(false);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const update = () => setIsRtl(html.getAttribute('dir') === 'rtl');
    update();
    const observer = new MutationObserver(update);
    observer.observe(html, { attributes: true, attributeFilter: ['dir'] });
    return () => observer.disconnect();
  }, []);

  return isRtl;
}
